// src/rms/data/seed.js
// ============================================================================
// Demo data
// ============================================================================
// Provisioning a new operator seeds a small, believable book of business for
// their chosen vertical: a handful of assets with realistic custom fields, a
// few customers, and some reservations spread across the calendar — including
// one already checked out, so the dashboard and check-in flow have something
// to show on first load.
//
// This is demo content, not fixtures: it is generated per vertical from the
// preset, so a 13th vertical gets sensible seed data without touching this file
// beyond adding a row to ASSET_SAMPLES.
// ============================================================================

import i18n from "../i18n";
import db from "./index";
import { getPreset, OCCUPANCY_CONTINUOUS } from "../verticals/presets";
import { applyTax } from "../pricing/taxEngine";
import { loadTaxConfig } from "./taxService";

/**
 * Sample content, in the operator's language.
 *
 * Translated ONCE, here, at the moment it is written -- unlike ad copy, which
 * is translated every time it is rendered. The difference is ownership: the
 * moment this lands it stops being our sample and becomes their records, which
 * they can rename and edit. Re-translating it on read would overwrite what
 * somebody typed.
 *
 * Only prose gets this. "Bosch 14in Concrete Saw", "DeWalt DCH614", "Maya
 * Okonkwo" and "Harbor Point" stay exactly as they are -- a French rental shop
 * still rents a Bosch GCD14, and translating a proper noun is how sample data
 * starts looking machine-made.
 */
const st = (key, fallback) => i18n.t(key, { defaultValue: fallback });

const day = 864e5;
const iso = (ms) => new Date(ms).toISOString();
const nowIsoDate = () => new Date().toISOString();

// Per-vertical sample assets: [name, category, rate cents, deposit cents, custom fields]
const ASSET_SAMPLES = {
  car_rental: [
    ["2024 Toyota Corolla", "Economy", 5900, 25000, { make: "Toyota", model: "Corolla", year: 2024, vin: "2T1BURHE0KC123456", license_plate: "BXT-4471", color: "Silver", transmission: "automatic", fuel_type: "gasoline", seats: 5, odometer: 31450, fuel_level: "full" }],
    ["2023 Honda CR-V", "SUV", 8900, 30000, { make: "Honda", model: "CR-V", year: 2023, vin: "7FARW2H51NE045512", license_plate: "CFG-8823", color: "Blue", transmission: "automatic", fuel_type: "gasoline", seats: 5, odometer: 48210, fuel_level: "3_4" }],
    ["2025 Ford Transit", "Van", 12900, 40000, { make: "Ford", model: "Transit 250", year: 2025, vin: "1FTBR1C81PKA33219", license_plate: "VAN-1140", color: "White", transmission: "automatic", fuel_type: "diesel", seats: 3, odometer: 12980, fuel_level: "full" }],
    ["2024 Tesla Model 3", "Premium", 14900, 50000, { make: "Tesla", model: "Model 3", year: 2024, vin: "5YJ3E1EA8PF556677", license_plate: "EV-2277", color: "Black", transmission: "automatic", fuel_type: "electric", seats: 5, odometer: 8740, fuel_level: "full" }],
  ],
  boat_rental: [
    ["Bayliner VR5 — 'Rascal'", "Bowrider", 34900, 50000, { make: "Bayliner", model: "VR5", year: 2022, hull_id: "BIYK21AC122", registration_number: "FL-4471-KM", length_ft: 21, engine_type: "sterndrive", engine_hours: 412, capacity_persons: 8, trailer_included: true, fuel_level: "full" }],
    ["Sun Tracker Party Barge 22", "Pontoon", 42900, 60000, { make: "Sun Tracker", model: "Party Barge 22 DLX", year: 2023, hull_id: "BUJ88123L223", registration_number: "FL-9902-AB", length_ft: 22, engine_type: "outboard", engine_hours: 268, capacity_persons: 12, trailer_included: false, fuel_level: "3_4" }],
    ["Catalina 22 — 'Windward'", "Sailboat", 28900, 45000, { make: "Catalina", model: "22 Sport", year: 2019, hull_id: "CTYK4471J819", registration_number: "FL-1188-SL", length_ft: 22, engine_type: "sail_auxiliary", engine_hours: 96, capacity_persons: 6, trailer_included: true, fuel_level: "1_2" }],
  ],
  atv_rental: [
    ["Polaris Sportsman 570 #1", "ATV", 15900, 40000, { make: "Polaris", model: "Sportsman 570", year: 2023, vin: "4XASEA507PA123441", engine_cc: 567, machine_type: "atv", seats: 1, engine_hours: 214, fuel_level: "full" }],
    ["Polaris Sportsman 570 #2", "ATV", 15900, 40000, { make: "Polaris", model: "Sportsman 570", year: 2023, vin: "4XASEA507PA123442", engine_cc: 567, machine_type: "atv", seats: 1, engine_hours: 189, fuel_level: "3_4" }],
    ["Can-Am Maverick X3", "UTV", 29900, 75000, { make: "Can-Am", model: "Maverick X3 Max", year: 2024, vin: "3JBVXAW48RJ000221", engine_cc: 900, machine_type: "utv_side_by_side", seats: 4, engine_hours: 87, fuel_level: "full" }],
  ],
  bicycle_rental: [
    ["City Cruiser #14", "City", 1200, 0, { bike_type: "city_hybrid", frame_size: "m", wheel_size: "700c", gear_count: 7, is_ebike: false, lock_code: "4471" }],
    ["City Cruiser #15", "City", 1200, 0, { bike_type: "city_hybrid", frame_size: "l", wheel_size: "700c", gear_count: 7, is_ebike: false, lock_code: "8823" }],
    ["Trek Verve+ E-Bike #3", "E-bike", 2900, 5000, { bike_type: "e_bike", frame_size: "m", wheel_size: "700c", gear_count: 9, is_ebike: true, battery_charge: 92, lock_code: "1140" }],
    ["Kids Trailer Bike #2", "Kids", 800, 0, { bike_type: "kids", frame_size: "xs", wheel_size: "20in", gear_count: 1, is_ebike: false, lock_code: "2277" }],
  ],
  storage_unit: [
    ["Unit A-104", "Small", 6500, 5000, { unit_size: "5x10", width_ft: 5, depth_ft: 10, height_ft: 8, floor: "ground", climate_controlled: false, drive_up_access: true, door_type: "roll_up", gate_zone: "A", access_code: "4471" }],
    ["Unit B-212", "Medium", 12500, 5000, { unit_size: "10x10", width_ft: 10, depth_ft: 10, height_ft: 8, floor: "2nd", climate_controlled: true, drive_up_access: false, door_type: "swing", gate_zone: "B", access_code: "8823" }],
    ["Unit C-008", "Large", 21500, 10000, { unit_size: "10x20", width_ft: 10, depth_ft: 20, height_ft: 10, floor: "ground", climate_controlled: true, drive_up_access: true, door_type: "roll_up", gate_zone: "C", access_code: "1140" }],
    ["Locker L-31", "Locker", 3500, 2500, { unit_size: "locker", width_ft: 3, depth_ft: 3, height_ft: 4, floor: "ground", climate_controlled: true, drive_up_access: false, door_type: "locker_door", gate_zone: "A", access_code: "2277" }],
  ],
  tool_rental: [
    ["DeWalt 60V Rotary Hammer", "Concrete", 6500, 15000, { tool_type: "Rotary hammer", manufacturer: "DeWalt", model: "DCH614", power_source: "battery", voltage: "60V", battery_charge: 100, runtime_hours: 142 }],
    ["Honda EU2200i Generator", "Power", 8900, 25000, { tool_type: "Generator", manufacturer: "Honda", model: "EU2200i", power_source: "gas", voltage: "2200W", runtime_hours: 388 }],
    ["Bosch 14in Concrete Saw", "Concrete", 9500, 30000, { tool_type: "Concrete saw", manufacturer: "Bosch", model: "GCD14", power_source: "gas", runtime_hours: 211 }],
    ["Wacker Neuson Plate Compactor", "Compaction", 7500, 20000, { tool_type: "Plate compactor", manufacturer: "Wacker Neuson", model: "WP1550", power_source: "gas", runtime_hours: 507 }],
  ],
  party_equipment: [
    ["20x30 Frame Tent", "Tent", 45000, 50000, { item_type: "tent_marquee", quantity_in_set: 1, dimensions: "20ft x 30ft", capacity: 60, color: "White", setup_required: true, setup_time_minutes: 120 }],
    ["Round Tables (set of 10)", "Table", 12000, 10000, { item_type: "table", quantity_in_set: 10, dimensions: "60in round", capacity: 8, color: "White", setup_required: false }],
    ["Folding Chairs (set of 100)", "Chair", 15000, 10000, { item_type: "chair", quantity_in_set: 100, color: "White", setup_required: false }],
    ["QSC K12.2 PA System", "Sound", 22000, 40000, { item_type: "sound_system", quantity_in_set: 1, capacity: 250, setup_required: true, setup_time_minutes: 45 }],
  ],
  photo_av_gear: [
    ["Sony FX3 Body", "Camera", 18500, 150000, { gear_type: "camera_body", brand: "Sony", model: "FX3", serial_number: "SN-FX3-004471", mount: "E-mount", shutter_count: 18400, replacement_value_cents: 389900, case_included: true }],
    ["Canon RF 24-70mm f/2.8", "Lens", 8500, 80000, { gear_type: "lens", brand: "Canon", model: "RF 24-70mm f/2.8L", serial_number: "SN-RF2470-8823", mount: "RF", replacement_value_cents: 229900, case_included: true }],
    ["Aputure 600d Pro", "Lighting", 11500, 90000, { gear_type: "lighting", brand: "Aputure", model: "LS 600d Pro", serial_number: "SN-AP600-1140", replacement_value_cents: 189900, case_included: true }],
    ["DJI RS 4 Pro Gimbal", "Support", 7500, 60000, { gear_type: "gimbal", brand: "DJI", model: "RS 4 Pro", serial_number: "SN-RS4-2277", replacement_value_cents: 99900, case_included: true }],
  ],
  costume_formalwear: [
    ["Classic Black Tuxedo 40R", "Tuxedo", 9900, 20000, { garment_type: "tuxedo", size: "40R", size_system: "us", color: "Black", fabric: "Wool", cleaning_status: "ready", wear_count: 23 }],
    ["Navy Slim Suit 38S", "Suit", 8500, 18000, { garment_type: "suit", size: "38S", size_system: "us", color: "Navy", fabric: "Wool blend", cleaning_status: "ready", wear_count: 11 }],
    ["Burgundy Evening Gown 8", "Gown", 12500, 25000, { garment_type: "gown", size: "8", size_system: "us", color: "Burgundy", fabric: "Satin", cleaning_status: "ready", wear_count: 7 }],
    ["Victorian Costume — Large", "Costume", 7500, 15000, { garment_type: "costume", size: "L", size_system: "alpha", color: "Green", fabric: "Velvet", cleaning_status: "needs_cleaning", wear_count: 34 }],
  ],
  camping_gear: [
    ["MSR Hubba Hubba 2P Tent", "Tent", 3500, 7500, { gear_type: "tent", brand: "MSR", model: "Hubba Hubba NX", capacity_persons: 2, season_rating: "3-season", weight_kg: 1.7, trip_count: 18, cleaning_status: "ready" }],
    ["REI Magma 15 Sleeping Bag", "Sleep", 1800, 5000, { gear_type: "sleeping_bag", brand: "REI", model: "Magma 15", capacity_persons: 1, season_rating: "15°F", weight_kg: 0.9, trip_count: 25, cleaning_status: "ready" }],
    ["Osprey Atmos 65 Pack", "Pack", 2200, 6000, { gear_type: "backpack", brand: "Osprey", model: "Atmos AG 65", weight_kg: 2.1, trip_count: 31, cleaning_status: "ready" }],
    ["Old Town Kayak — Yellow", "Paddle", 5500, 15000, { gear_type: "kayak_canoe", brand: "Old Town", model: "Loon 106", capacity_persons: 1, weight_kg: 26, trip_count: 44, cleaning_status: "ready" }],
  ],
  rv_rental: [
    ["2023 Winnebago Minnie 22M", "Class C", 24900, 150000, { rv_class: "class_c", make: "Winnebago", model: "Minnie Winnie 22M", year: 2023, vin: "1FDXE4FS8PDC12345", license_plate: "RV-4471", length_ft: 24, sleeps: 6, odometer: 28400, generator_hours: 142, propane_level: "full", fresh_water_level: 100, waste_tank_status: "empty_dumped", fuel_level: "full" }],
    ["2024 Ford Transit Campervan", "Class B", 19900, 120000, { rv_class: "class_b_campervan", make: "Ford", model: "Transit Trail", year: 2024, vin: "1FTBW3XG4PKA55112", license_plate: "RV-8823", length_ft: 20, sleeps: 2, odometer: 14100, generator_hours: 38, propane_level: "3_4", fresh_water_level: 80, waste_tank_status: "empty_dumped", fuel_level: "full" }],
    ["2022 Jayco Jay Flight 24RBS", "Trailer", 15900, 100000, { rv_class: "travel_trailer", make: "Jayco", model: "Jay Flight 24RBS", year: 2022, vin: "1UJBJ0BP4N1AB1234", license_plate: "TR-1140", length_ft: 28, sleeps: 5, odometer: 0, generator_hours: 0, propane_level: "full", fresh_water_level: 100, waste_tank_status: "empty_dumped", fuel_level: "empty" }],
  ],
  construction_equipment: [
    ["CAT 305 Mini Excavator", "Excavator", 39900, 200000, { equipment_type: "excavator", make: "Caterpillar", model: "305 CR", year: 2023, serial_number: "CAT0305XLKM04471", engine_hours: 1284, operating_weight_kg: 5200, transport_class: "tilt_deck", required_certification: "excavator", last_service_hours: 1200, service_interval_hours: 250, fuel_level: "full" }],
    ["Genie S-45 Boom Lift", "Aerial", 44900, 250000, { equipment_type: "boom_lift", make: "Genie", model: "S-45 XC", year: 2022, serial_number: "GS45XC-8823", engine_hours: 2140, operating_weight_kg: 7100, transport_class: "lowboy", required_certification: "aerial_lift_mewp", last_service_hours: 2000, service_interval_hours: 500, fuel_level: "3_4" }],
    ["Bobcat S76 Skid Steer", "Skid steer", 34900, 180000, { equipment_type: "skid_steer", make: "Bobcat", model: "S76", year: 2024, serial_number: "B3BM11140", engine_hours: 640, operating_weight_kg: 3900, transport_class: "tilt_deck", required_certification: "skid_steer", last_service_hours: 500, service_interval_hours: 250, fuel_level: "full" }],
    ["Hamm HD12 Roller", "Compaction", 29900, 150000, { equipment_type: "compactor_roller", make: "Hamm", model: "HD 12 VV", year: 2021, serial_number: "HD12-2277", engine_hours: 3320, operating_weight_kg: 2600, transport_class: "tilt_deck", required_certification: "none", last_service_hours: 3250, service_interval_hours: 250, fuel_level: "1_2" }],
  ],
};

const CUSTOMER_SAMPLES = [
  { first_name: "Maya", last_name: "Okonkwo", email: "maya.okonkwo@example.com", phone: "+1 415 555 0142", city: "Oakland" },
  { first_name: "Daniel", last_name: "Reyes", email: "d.reyes@example.com", phone: "+1 415 555 0188", city: "San Mateo" },
  { first_name: "Priya", last_name: "Raghavan", email: "priya.r@example.com", phone: "+1 628 555 0119", city: "Berkeley" },
  { first_name: "Tomas", last_name: "Lindqvist", email: "tomas.l@example.com", phone: "+1 510 555 0177", city: "Alameda" },
  { first_name: "Aisha", last_name: "Bello", email: "aisha.bello@example.com", phone: "+1 707 555 0163", city: "Richmond" },
];

/**
 * Writes one demo row. Every insert in this file goes through here, so the flag
 * cannot be forgotten on a table added later -- the alternative, repeating
 * `is_demo: true` at 27 call sites, is one omission away from a demo row that
 * looks like the operator's own.
 *
 * The flag lives ON THE ROW rather than in a list of ids on the operator, which
 * is what this used to do. That list shared one jsonb blob with setupDismissed
 * and setupCollapsed, both written client-side as {...config_overrides, key},
 * so a single write from a stale operator dropped it and every demo row started
 * counting as the operator's own work.
 */
async function createDemo(collection, row) {
  return db.create(collection, { ...row, is_demo: true });
}

/**
 * Seeds a freshly provisioned operator. Idempotent: if assets already exist for
 * this operator it does nothing, so a page refresh cannot duplicate the demo.
 */
export async function seedOperator(operator) {
  const existing = (await db.list("assets", { operator_id: operator.id })).data || [];
  if (existing.length > 0) return { seeded: false };

  const preset = getPreset(operator.vertical_key);
  if (!preset) return { seeded: false };

  const isContinuous = preset.occupancyModel === OCCUPANCY_CONTINUOUS;
  const ops = preset.operations || {};

  // ---- operational defaults from the vertical -------------------------------
  // Seeded once, then owned by the operator. Nothing reads the preset constants
  // again after this point.
  await db.updateOperator(operator.id, {
    measurement_system: ops.measurementSystem || "metric",
    default_turnaround_minutes: ops.turnaroundMinutes || 0,
    default_booking_mode: ops.bookingMode || "request",
  });

  // ---- tax --------------------------------------------------------------
  // Deliberately nothing. The demo used to seed a "Sample state tax" and a
  // "Sample city tax" so an invoice would show a multi-line breakdown, guarded
  // by "skip if onboarding already set a rate up".
  //
  // Onboarding no longer asks about tax -- it is configured from the dashboard,
  // where there is room to get it right -- so that guard would now be true
  // every time and every new business would start with two invented rates on
  // its invoices. That is precisely the outcome the whole tax design exists to
  // avoid: a rate we guessed and got wrong becomes the operator's liability.
  //
  // With no rates, finalizeInvoice writes tax_cents 0, Billing shows the "no
  // tax rates configured" banner, and the dashboard setup checklist carries
  // the item until a real rate is entered. Every one of those already existed;
  // none of them lies.

  let cancellationPolicyId = null;
  if (ops.cancellationPolicy) {
    const policy = (
      await createDemo("cancellationPolicies", {
        operator_id: operator.id,
        name: ops.cancellationPolicy.name,
        description: ops.cancellationPolicy.description,
        tiers: ops.cancellationPolicy.tiers || [],
        is_default: true,
      })
    ).data;
    cancellationPolicyId = policy.id;
  }

  for (const s of ops.usageFeeSchedules || []) {
    await createDemo("usageFeeSchedules", {
      operator_id: operator.id,
      name: s.name,
      metric_key: s.metricKey,
      unit: s.unit,
      included_per_period: s.includedPerPeriod,
      period: s.period,
      flat_cents_per_unit: s.flatCentsPerUnit || 0,
      tiers: s.tiers || [],
      is_active: true,
    });
  }

  const addonIdsByName = new Map();
  for (let i = 0; i < (ops.addons || []).length; i += 1) {
    const a = ops.addons[i];
    const created = await createDemo("addons", {
      operator_id: operator.id,
      asset_id: null,
      asset_group_id: null,
      name: a.name,
      description: a.description || null,
      price_cents: a.priceCents,
      charge_type: a.chargeType,
      is_required: !!a.isRequired,
      is_hidden: !!a.isHidden,
      taxable: a.taxable !== false,
      max_quantity: a.maxQuantity || null,
      default_quantity: 1,
      position: i,
      is_active: true,
    });
    // Kept so a question can point at one by name. Addons are seeded before
    // questions for exactly this reason -- the preset names the addon, the
    // database wants its id, and the resolution has to happen somewhere.
    if (created?.data?.id) addonIdsByName.set(a.name, created.data.id);
  }

  for (let i = 0; i < (ops.checkoutQuestions || []).length; i += 1) {
    const q = ops.checkoutQuestions[i];

    // A question earns a trigger_value only if it actually has a consequence.
    // Null means "this is just a note", which is most of them, and is what
    // stops the client from looking for follow-ups that do not exist.
    const hasConsequence = !!q.triggersAddon || (q.followUp || []).length > 0;

    await createDemo("checkoutQuestions", {
      operator_id: operator.id,
      asset_id: null,
      asset_group_id: null,
      prompt: q.prompt,
      help_text: q.help_text || null,
      question_type: q.question_type,
      options: q.options || [],
      is_required: !!q.is_required,
      trigger_value: hasConsequence ? q.triggerValue || "Yes" : null,
      triggers_addon_id: q.triggersAddon ? addonIdsByName.get(q.triggersAddon) || null : null,
      follow_up_fields: q.followUp || [],
      position: i,
      is_active: true,
    });
  }

  // Automatic emails, in this trade's words. Seeded active: an operator who
  // does not want them turns them off, rather than having to discover that
  // confirmations exist and go and switch them on.
  for (const tpl of ops.messageTemplates || []) {
    await createDemo("messageTemplates", {
      operator_id: operator.id,
      template_key: tpl.template_key,
      subject: tpl.subject,
      body: tpl.body,
      is_active: true,
    });
  }

  for (let i = 0; i < (ops.rateRules || []).length; i += 1) {
    const r = ops.rateRules[i];
    await createDemo("rateRules", {
      operator_id: operator.id,
      name: r.name,
      category: r.category,
      scope_type: "all",
      scope_values: [],
      travel_starts_on: r.travelStartsOn || null,
      travel_ends_on: r.travelEndsOn || null,
      search_starts_on: r.searchStartsOn || null,
      search_ends_on: r.searchEndsOn || null,
      days_of_week: r.daysOfWeek || [],
      adjustment_direction: r.direction || null,
      adjustment_kind: r.kind || null,
      adjustment_value: r.value ?? null,
      tiers: r.tiers || [],
      min_duration_units: r.minDurationUnits || null,
      utilization_min_pct: r.utilizationMinPct ?? null,
      utilization_max_pct: r.utilizationMaxPct ?? null,
      priority: 100 + i,
      is_exclusive: false,
      is_active: true,
    });
  }

  const location = (
    await createDemo("locations", {
      operator_id: operator.id,
      name: isContinuous ? "Main Facility" : "Main Branch",
      city: "Oakland",
      region: "CA",
      country: "US",
      is_default: true,
      is_active: true,
    })
  ).data;

  // ---- assets ----
  const samples = ASSET_SAMPLES[operator.vertical_key] || [];
  const assets = [];
  for (let i = 0; i < samples.length; i += 1) {
    const [name, category, rate, deposit, custom] = samples[i];
    const asset = (
      await createDemo("assets", {
        operator_id: operator.id,
        location_id: location.id,
        name,
        asset_code: `${preset.key.slice(0, 3).toUpperCase()}-${String(i + 1).padStart(3, "0")}`,
        internal_code: `${preset.key.slice(0, 3).toUpperCase()}-INT-${1000 + i}`,
        serial_number: custom.vin || custom.serial_number || custom.hull_id || null,
        category,
        status: "available",
        base_rate_cents: rate,
        rate_unit: preset.rateDefaults?.primaryUnit || "day",
        deposit_cents: deposit,
        photos: [],
        custom_fields: custom,
        is_active: true,
        is_published: true,
        published_at: nowIsoDate(),
        position: i,
        turnaround_minutes: ops.turnaroundMinutes || 0,
        booking_mode: ops.bookingMode || "request",
        cancellation_policy_id: cancellationPolicyId,
        reservation_deposit_pct: 25,
        reservation_deposit_cents: 5000,
        replacement_value_cents: custom.replacement_value_cents || rate * 40,
        usage_fee_schedules: {},
      })
    ).data;
    assets.push(asset);
  }

  // ---- a pooled group, so the class-vs-unit model is visible ---------------
  // Three interchangeable copies of the first sample under one listing. This is
  // the case a per-row model gets wrong: one booking should leave two free, not
  // block the listing.
  let demoGroup = null;
  if (ops.grouping?.enabled && assets.length) {
    const template = assets[0];
    demoGroup = (
      await createDemo("assetGroups", {
        operator_id: operator.id,
        location_id: location.id,
        name: `${template.name} ${st("seed.poolSuffix", "(pool)")}`,
        group_code: `${preset.key.slice(0, 3).toUpperCase()}-GRP-01`,
        description: ops.grouping.hint || null,
        category: template.category,
        base_rate_cents: template.base_rate_cents,
        rate_unit: template.rate_unit,
        deposit_cents: template.deposit_cents,
        custom_fields: template.custom_fields,
        photos: [],
        is_published: true,
        published_at: nowIsoDate(),
        position: 0,
        is_active: true,
      })
    ).data;

    await db.update("assets", template.id, { group_id: demoGroup.id, is_group_primary: true });
    assets[0] = (await db.get("assets", template.id)).data;

    for (let n = 2; n <= 3; n += 1) {
      const clone = (
        await createDemo("assets", {
          ...template,
          id: undefined,
          name: `${template.name} #${n}`,
          asset_code: `${template.asset_code}-${n}`,
          internal_code: `${template.internal_code}-${n}`,
          serial_number: template.serial_number ? `${template.serial_number}-${n}` : null,
          group_id: demoGroup.id,
          is_group_primary: false,
          position: n,
        })
      ).data;
      assets.push(clone);
    }
  }

  // ---- customers ----
  const customers = [];
  for (const c of CUSTOMER_SAMPLES) {
    const customer = (
      await createDemo("customers", {
        operator_id: operator.id,
        ...c,
        region: "CA",
        country: "US",
        id_verification: {
          verified_at: new Date(Date.now() - 30 * day).toISOString(),
          method: "drivers_license",
          license_number: `D${Math.floor(1000000 + Math.random() * 8999999)}`,
          insurance_on_file: true,
        },
        is_blocked: false,
      })
    ).data;
    customers.push(customer);
  }

  if (!assets.length || !customers.length) return { seeded: true, assets: assets.length };

  // ---- certifications (construction only) ----
  if (preset.workflow.certificationRequired) {
    // Read off the fleet rather than typed out again here, so the demo issues
    // exactly the tickets its own machines demand. Taking the first few from
    // the preset instead would hand someone a Forklift ticket nothing needs
    // while leaving the skid steer with no certified operator -- which looks
    // like the certification gate is broken when it is working perfectly.
    // "none" is the value meaning the machine needs no ticket at all.
    const certTypes = Array.from(
      new Set(
        assets
          .map((a) => a.custom_fields?.required_certification)
          .filter((v) => v && v !== "none")
      )
    );
    for (let i = 0; i < certTypes.length; i += 1) {
      await createDemo("certifications", {
        operator_id: operator.id,
        customer_id: customers[i % customers.length].id,
        certification_type: certTypes[i],
        holder_name: `${customers[i % customers.length].first_name} ${customers[i % customers.length].last_name}`,
        certificate_number: `CERT-${4471 + i}`,
        issuing_authority: "National Safety Council",
        issued_at: new Date(Date.now() - 400 * day).toISOString().slice(0, 10),
        expires_at: new Date(Date.now() + 300 * day).toISOString().slice(0, 10),
        status: "verified",
        verified_at: new Date(Date.now() - 395 * day).toISOString(),
      });
    }
  }

  // ---- bookings ----
  const t = Date.now();

  if (isContinuous) {
    // Storage: two units already occupied on month-to-month leases, one unit
    // free, so the occupancy board and the recurring biller both have data.
    for (let i = 0; i < Math.min(2, assets.length); i += 1) {
      const asset = assets[i];
      const customer = customers[i];
      const movedIn = new Date(t - (60 + i * 45) * day);

      const reservation = (
        await createDemo("reservations", {
          operator_id: operator.id,
          asset_id: asset.id,
          customer_id: customer.id,
          location_id: location.id,
          reservation_number: `RES-${String(i + 1).padStart(4, "0")}`,
          status: "active",
          starts_at: movedIn.toISOString(),
          ends_at: null,
          rate_snapshot: { rate_unit: "month", units: 1, unit_cents: asset.base_rate_cents, subtotal_cents: asset.base_rate_cents },
          quoted_total_cents: asset.base_rate_cents,
          currency: operator.currency,
          source: "staff",
        })
      ).data;

      const agreement = (
        await createDemo("agreements", {
          operator_id: operator.id,
          reservation_id: reservation.id,
          asset_id: asset.id,
          customer_id: customer.id,
          agreement_number: `AGR-${String(i + 1).padStart(4, "0")}`,
          status: "open",
          checkout_at: movedIn.toISOString(),
          checkout_data: {},
          deposit_cents: asset.deposit_cents,
          deposit_status: "held",
        })
      ).data;

      const nextBill = new Date(t);
      nextBill.setMonth(nextBill.getMonth() + 1, 1);

      const subscription = (
        await createDemo("subscriptions", {
          operator_id: operator.id,
          customer_id: customer.id,
          asset_id: asset.id,
          reservation_id: reservation.id,
          status: i === 1 ? "past_due" : "active",
          billing_interval: "month",
          amount_cents: asset.base_rate_cents,
          currency: operator.currency,
          start_date: movedIn.toISOString().slice(0, 10),
          next_bill_date: nextBill.toISOString().slice(0, 10),
          prorate_first_period: true,
          late_fee_cents: preset.rateDefaults?.lateFeeCents || 2000,
          late_fee_grace_days: preset.rateDefaults?.lateFeeGraceDays ?? 5,
          lien_status: i === 1 ? "notice_sent" : "none",
          delinquent_since: i === 1 ? new Date(t - 20 * day).toISOString().slice(0, 10) : null,
        })
      ).data;

      const occupancy = (
        await createDemo("occupancies", {
          operator_id: operator.id,
          asset_id: asset.id,
          customer_id: customer.id,
          subscription_id: subscription.id,
          agreement_id: agreement.id,
          status: "active",
          moved_in_at: movedIn.toISOString(),
          lock_type: "tenant_padlock",
        })
      ).data;

      const credential = (
        await createDemo("accessCredentials", {
          operator_id: operator.id,
          occupancy_id: occupancy.id,
          asset_id: asset.id,
          customer_id: customer.id,
          credential_type: "gate_code",
          credential_value: String(asset.custom_fields.access_code || 1000 + i),
          label: st("seed.gateCode", "Facility gate code"),
          issued_at: movedIn.toISOString(),
          last_used_at: new Date(t - (i + 1) * day).toISOString(),
        })
      ).data;

      // A little gate traffic so the access log is not empty.
      for (let e = 0; e < 4; e += 1) {
        await createDemo("accessEvents", {
          operator_id: operator.id,
          asset_id: asset.id,
          occupancy_id: occupancy.id,
          customer_id: customer.id,
          credential_id: credential.id,
          event_type: e % 2 === 0 ? "entry" : "exit",
          gate_zone: asset.custom_fields.gate_zone || "A",
          source: "keypad",
          occurred_at: new Date(t - (e + 1) * 2 * day).toISOString(),
        });
      }

      await db.update("assets", asset.id, { status: "occupied" });
    }
  } else {
    // Discrete verticals: one live rental, one upcoming, one completed.
    const [a0, a1, a2] = assets;

    if (a0) {
      const start = t - 2 * day;
      const end = t + 2 * day;
      const reservation = (
        await createDemo("reservations", {
          operator_id: operator.id,
          asset_id: a0.id,
          customer_id: customers[0].id,
          location_id: location.id,
          reservation_number: "RES-0001",
          status: "checked_out",
          starts_at: iso(start),
          ends_at: iso(end),
          rate_snapshot: { rate_unit: a0.rate_unit, units: 4, unit_cents: a0.base_rate_cents, subtotal_cents: 4 * a0.base_rate_cents },
          quoted_total_cents: 4 * a0.base_rate_cents,
          currency: operator.currency,
          source: "staff",
        })
      ).data;

      await createDemo("agreements", {
        operator_id: operator.id,
        reservation_id: reservation.id,
        asset_id: a0.id,
        customer_id: customers[0].id,
        agreement_number: "AGR-0001",
        status: "open",
        checkout_at: iso(start),
        checkout_data: pickCapturedFields(preset, a0, "checkout"),
        deposit_cents: a0.deposit_cents,
        deposit_status: a0.deposit_cents ? "held" : "none",
        waiver_accepted_at: preset.workflow.waiverRequired ? iso(start) : null,
      });

      await db.update("assets", a0.id, { status: "rented" });
    }

    if (a1) {
      await createDemo("reservations", {
        operator_id: operator.id,
        asset_id: a1.id,
        customer_id: customers[1].id,
        location_id: location.id,
        reservation_number: "RES-0002",
        status: "confirmed",
        starts_at: iso(t + 3 * day),
        ends_at: iso(t + 6 * day),
        rate_snapshot: { rate_unit: a1.rate_unit, units: 3, unit_cents: a1.base_rate_cents, subtotal_cents: 3 * a1.base_rate_cents },
        quoted_total_cents: 3 * a1.base_rate_cents,
        currency: operator.currency,
        source: "online",
      });
      await db.update("assets", a1.id, { status: "reserved" });
    }

    if (a2) {
      const start = t - 12 * day;
      const end = t - 9 * day;
      const reservation = (
        await createDemo("reservations", {
          operator_id: operator.id,
          asset_id: a2.id,
          customer_id: customers[2].id,
          location_id: location.id,
          reservation_number: "RES-0003",
          status: "completed",
          starts_at: iso(start),
          ends_at: iso(end),
          rate_snapshot: { rate_unit: a2.rate_unit, units: 3, unit_cents: a2.base_rate_cents, subtotal_cents: 3 * a2.base_rate_cents },
          quoted_total_cents: 3 * a2.base_rate_cents,
          currency: operator.currency,
          source: "phone",
        })
      ).data;

      const agreement = (
        await createDemo("agreements", {
          operator_id: operator.id,
          reservation_id: reservation.id,
          asset_id: a2.id,
          customer_id: customers[2].id,
          agreement_number: "AGR-0002",
          status: "closed",
          checkout_at: iso(start),
          checkin_at: iso(end),
          checkout_data: pickCapturedFields(preset, a2, "checkout"),
          checkin_data: pickCapturedFields(preset, a2, "checkin"),
          deposit_cents: a2.deposit_cents,
          deposit_status: a2.deposit_cents ? "released" : "none",
        })
      ).data;

      const subtotal = 3 * a2.base_rate_cents;

      // Priced through the real engine rather than hardcoded to zero tax, so
      // the demo invoice shows the breakdown an operator will actually see.
      const taxConfig = await loadTaxConfig(
        (await db.get("operators", operator.id)).data || operator
      );
      const priced = applyTax(
        [
          {
            kind: "rental",
            description: `${a2.name} — 3 × ${a2.rate_unit}`,
            quantity: 3,
            unit_cents: a2.base_rate_cents,
            amount_cents: subtotal,
            tax_group_id: taxConfig.groupFor("rental"),
            taxable: taxConfig.groupFor("rental") !== null,
          },
        ],
        taxConfig
      );

      const invoice = (
        await createDemo("invoices", {
          operator_id: operator.id,
          customer_id: customers[2].id,
          agreement_id: agreement.id,
          invoice_number: "INV-0001",
          status: "paid",
          line_items: priced.lines,
          subtotal_cents: priced.subtotal_cents,
          discount_cents: 0,
          tax_cents: priced.tax_cents,
          total_cents: priced.total_cents,
          amount_paid_cents: priced.total_cents,
          currency: operator.currency,
          tax_breakdown: priced.tax_breakdown,
          issued_at: iso(end),
          paid_at: iso(end),
        })
      ).data;

      await createDemo("payments", {
        operator_id: operator.id,
        invoice_id: invoice.id,
        customer_id: customers[2].id,
        amount_cents: priced.total_cents,
        currency: operator.currency,
        method: "card",
        status: "captured",
        captured_at: iso(end),
      });
    }

    // Construction: a delivery already on the dispatch board.
    if (preset.workflow.deliveryEnabled && a1) {
      await createDemo("deliveries", {
        operator_id: operator.id,
        asset_id: a1.id,
        direction: "delivery",
        status: "scheduled",
        scheduled_at: iso(t + 3 * day),
        window_start: iso(t + 3 * day),
        window_end: iso(t + 3 * day + 4 * 36e5),
        site_name: "Harbor Point — Phase 2",
        address_line1: "1400 Embarcadero West",
        city: "Oakland",
        region: "CA",
        postal_code: "94607",
        site_contact_name: "Site super — Ray Mendez",
        site_contact_phone: "+1 510 555 0190",
        site_notes: st("seed.siteNotes", "Enter via Gate 3. Overhead lines on the north edge."),
        transport_class: a1.custom_fields?.transport_class || "Tilt deck",
        permit_required: (a1.custom_fields?.operating_weight_kg || 0) > 6000,
        fee_cents: 25000,
      });
    }
  }

  // ---- a scheduled blackout, so the calendar has something to explain ------
  const lastAsset = assets[assets.length - 1];
  if (lastAsset) {
    await createDemo("availabilityBlocks", {
      operator_id: operator.id,
      asset_id: lastAsset.id,
      asset_group_id: null,
      starts_at: iso(t + 10 * day),
      ends_at: iso(t + 14 * day),
      block_type: "maintenance",
      reason: isContinuous
        ? st("seed.blockDoorLock", "Door and lock replacement")
        : st("seed.blockService", "Scheduled annual service"),
      blocks_customer_booking: true,
      blocks_staff_booking: true,
    });
  }

  // ---- nothing to record --------------------------------------------------
  // This block used to snapshot every id the seed had just written into
  // rms_operators.config_overrides->'seeded', so the dashboard could subtract
  // them from the setup checklist. It was the right idea in the wrong place:
  // that jsonb blob is also where setupDismissed and setupCollapsed live, and
  // both are written client-side as {...config_overrides, key: value} from a
  // React closure. One write from a stale operator dropped the whole list --
  // after which a business that had entered nothing was congratulated with
  // "7 of 9 done" for sample vehicles it had never touched.
  //
  // createDemo now stamps is_demo on the row itself, which no unrelated write
  // to a different table can clobber, and which the UI can read to say what a
  // row is rather than only to count it.

  return { seeded: true, assets: assets.length, customers: customers.length, group: demoGroup?.name || null };
}

/** Plausible values for the fields this vertical captures at a given leg. */
function pickCapturedFields(preset, asset, phase) {
  const out = {};
  for (const field of preset.assetSchema) {
    if (!(field.capturedAt || []).includes(phase)) continue;
    const current = asset.custom_fields?.[field.key];
    if (current === undefined) continue;
    // Meters advance between checkout and check-in.
    if (phase === "checkin" && typeof current === "number") {
      out[field.key] = Math.round(current * 1.03);
    } else {
      out[field.key] = current;
    }
  }
  return out;
}

export default seedOperator;
